/**
 * The ranking board, as a diagram that moves once.
 *
 * A listing sitting fourth tops up, passes three rows, and takes first place.
 * The whole sequence is 1.36 seconds; the longest single move is 220ms. It
 * plays once, when the board has scrolled properly into view, and then holds
 * its finished state — there is a quiet Replay for anyone who missed it.
 *
 * Only transforms and opacities animate. Every figure that changes is two
 * stacked spans in a fixed-width tabular box, one fading out under the other,
 * so no digit ever shifts the column beside it mid-move.
 *
 * Anyone who has asked for reduced motion is shown the finished frame
 * directly: the reordered board, the new total, the closing narration.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { Money } from './Money';
import { CompanyMark } from './ui';
import { MARK_COLOURS } from '../lib/format';
import {
  CHALLENGER,
  CHALLENGER_INDEX,
  EXAMPLE,
  EXAMPLE_BOARD,
  LEADER,
  type ExampleListing,
} from '../lib/about-example';

/** Every part of the board that moves, and whether it has moved yet. */
interface Phase {
  /** The indigo ring and one-pixel lift on the challenger. */
  readonly ring: boolean;
  /** The top-up chip above it. */
  readonly chip: boolean;
  /** The challenger's total, swapped from what it held to what it now holds. */
  readonly raised: boolean;
  /** The reorder itself. */
  readonly moved: boolean;
  /** The rank numerals: 4→1, and 1→2, 2→3, 3→4. */
  readonly ranks: boolean;
  /** The leader wash, moved from the old first row to the new one. */
  readonly wash: boolean;
  /** The narration under the board. */
  readonly narrated: boolean;
}

const REST: Phase = {
  ring: false,
  chip: false,
  raised: false,
  moved: false,
  ranks: false,
  wash: false,
  narrated: false,
};

const FINISHED: Phase = {
  ring: false,
  chip: false,
  raised: true,
  moved: true,
  ranks: true,
  wash: true,
  narrated: true,
};

/**
 * The timeline, in milliseconds from the first frame. Durations and easings
 * live in the stylesheet beside the properties they belong to; this table only
 * says when each change is made.
 */
const TIMELINE: readonly (readonly [number, Partial<Phase>])[] = [
  [0, { ring: true }],
  [140, { chip: true }],
  [420, { raised: true }],
  [760, { moved: true, chip: false }],
  [870, { ranks: true }],
  [940, { wash: true }],
  [1040, { ring: false }],
  [1160, { narrated: true }],
];

/** True when the visitor has asked for less movement. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Where a row sits once the reorder is done. */
function finalRank(index: number): number {
  if (index === CHALLENGER_INDEX) return 1;
  return index < CHALLENGER_INDEX ? index + 2 : index + 1;
}

export function RankingBoard(): ReactNode {
  const still = useMemo(prefersReducedMotion, []);
  const [phase, setPhase] = useState<Phase>(still ? FINISHED : REST);
  const [resetting, setResetting] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const timers = useRef<number[]>([]);
  const frames = useRef<number[]>([]);

  const clear = useCallback(() => {
    for (const timer of timers.current) window.clearTimeout(timer);
    for (const frame of frames.current) window.cancelAnimationFrame(frame);
    timers.current = [];
    frames.current = [];
  }, []);

  const play = useCallback(() => {
    clear();
    setResetting(true);
    setPhase(REST);
    // Two frames: one to paint the rest state with transitions suppressed, so
    // a replay never runs the board backwards, and one to switch them back on
    // before the first change lands.
    frames.current.push(
      window.requestAnimationFrame(() => {
        frames.current.push(
          window.requestAnimationFrame(() => {
            setResetting(false);
            for (const [at, patch] of TIMELINE) {
              timers.current.push(
                window.setTimeout(() => {
                  setPhase((current) => ({ ...current, ...patch }));
                }, at),
              );
            }
          }),
        );
      }),
    );
  }, [clear]);

  useEffect(() => {
    if (still) return undefined;
    const node = rootRef.current;
    if (node === null || typeof IntersectionObserver !== 'function') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry === undefined || !entry.isIntersecting) return;
        observer.disconnect();
        play();
      },
      { threshold: 0.55 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      clear();
    };
  }, [clear, play, still]);

  return (
    <div
      className={`card rboard${resetting ? ' is-resetting' : ''}`}
      ref={rootRef}
      role="group"
      aria-label="A worked example: a listing tops up and takes first place"
    >
      {/* Visible, not only in the aria-label: the rows carry four-figure totals
          and real-looking column headers, so a sighted reader has no other way
          to tell this from the live board. */}
      <div className="rb-legend">
        <span className="eyebrow">Worked example</span>
        <span>Invented listings and figures, shown to explain the mechanic.</span>
      </div>

      <div className="rb-head" aria-hidden="true">
        <span className="eyebrow">
          <span className="is-wide">Rank</span>
          <span className="is-narrow">#</span>
        </span>
        <span className="eyebrow">Listing</span>
        <span className="eyebrow is-salary">Salary</span>
        <span className="eyebrow is-right">
          <span className="is-wide">Total paid</span>
          <span className="is-narrow">Paid</span>
        </span>
      </div>

      <div className="rb-stage">
        {EXAMPLE_BOARD.map((listing, index) => (
          <Row key={listing.company.slug} listing={listing} index={index} phase={phase} />
        ))}
      </div>

      <div className="rb-foot">
        <p className={`rb-cap${phase.narrated ? ' is-swapped' : ''}`}>
          <span className="before" aria-hidden={phase.narrated}>
            {CHALLENGER.company.name} holds <Money cents={CHALLENGER.totalCents} />. Passing{' '}
            <Money cents={LEADER.totalCents} /> takes <Money cents={EXAMPLE.firstPlaceTotalCents} />,
            so the top-up is <Money cents={EXAMPLE.topUpCents} />.
          </span>
          <span className="after" aria-hidden={!phase.narrated}>
            {CHALLENGER.company.name} is first on <Money cents={EXAMPLE.firstPlaceTotalCents} />{' '}
            paid. Northbeam keeps every dollar of its <Money cents={LEADER.totalCents} /> — taking
            the position back costs <Money cents={EXAMPLE.retakeTotalCents} />.
          </span>
        </p>
        {still ? null : (
          <button type="button" className="linkbtn rb-replay" onClick={play}>
            Replay
          </button>
        )}
      </div>
    </div>
  );
}

function Row({
  listing,
  index,
  phase,
}: {
  readonly listing: ExampleListing;
  readonly index: number;
  readonly phase: Phase;
}): ReactNode {
  const challenger = index === CHALLENGER_INDEX;
  const displaced = index < CHALLENGER_INDEX;
  const amountSwapped = challenger && phase.raised;
  const washOn = phase.wash ? challenger : index === 0;

  const classes = ['rb-row'];
  if (challenger) {
    classes.push('is-chal');
    if (phase.ring) classes.push('is-lifted');
    if (phase.moved) classes.push('is-moved');
  } else if (displaced && phase.moved) {
    classes.push('is-dropped');
  }

  // The three displaced rows leave 30ms apart, so the board settles rather
  // than snaps.
  const style: CSSProperties = {
    top: `calc(var(--rb-row-h) * ${index})`,
    ...(displaced ? { transitionDelay: `${index * 30}ms` } : {}),
  };

  return (
    <div className={classes.join(' ')} style={style}>
      <span className={`rb-wash${washOn ? ' is-on' : ''}`} aria-hidden="true" />
      {challenger ? (
        <>
          <span className={`rb-ring${phase.ring ? ' is-on' : ''}`} aria-hidden="true" />
          <span className={`rb-chip${phase.chip ? ' is-on' : ''}`} aria-hidden={!phase.chip}>
            {/* One inline child: a flex box would eat the spaces around it. */}
            <span>
              + <Money cents={EXAMPLE.topUpCents} /> top-up
            </span>
          </span>
        </>
      ) : null}

      <div className="rb-rank">
        <span className={`rb-num${phase.ranks ? ' is-swapped' : ''}`}>
          <span className="was" aria-hidden={phase.ranks}>
            {index + 1}
          </span>
          <span className="now" aria-hidden={!phase.ranks}>
            {finalRank(index)}
          </span>
        </span>
      </div>

      <div className="rb-listing">
        <CompanyMark company={listing.company} colour={MARK_COLOURS[listing.markIndex]} />
        <div className="rb-names">
          <div className="rb-title">{listing.title}</div>
          <div className="rb-sub">
            {listing.company.name} · {listing.scope}
          </div>
        </div>
      </div>

      <div className="rb-salary">{listing.salary}</div>

      <div className={`rb-amt${amountSwapped ? ' is-swapped' : ''}`}>
        <span className="was" aria-hidden={amountSwapped}>
          <Money cents={listing.totalCents} />
        </span>
        {challenger ? (
          <span className="now" aria-hidden={!amountSwapped}>
            <Money cents={EXAMPLE.firstPlaceTotalCents} />
          </span>
        ) : null}
      </div>
    </div>
  );
}
